import { gql } from '@apollo/client';
import { PACK_FRAGMENT, VARIANTS_PACK_FRAGMENT } from './items-context-queries';

export const MENU_ITEM_INGREDIENT_FRAGMENT = gql`
  fragment MenuInfo_MenuItemIngredientFields on menu_item_ingredients {
    cost_in_micros
    ingredient_name
    pack {
      ...ItemsContext_PackFields
    }
    variant_pack {
      ...ItemsContext_VariantsPackFields
    }
    uuid
  }

  ${PACK_FRAGMENT}
  ${VARIANTS_PACK_FRAGMENT}
`;

export const MENU_ITEM_INGREDIENT_METADATA_FRAGMENT = gql`
  fragment MenuInfo_MenuItemIngredientMetadataFields on menu_item_ingredient_metadata {
    menu_item_ingredient {
      ...MenuInfo_MenuItemIngredientFields
    }
    ingredient_metadata
    menu_item_uuid
    uuid
  }

  ${MENU_ITEM_INGREDIENT_FRAGMENT}
`;

const MENU_ITEM_FRAGMENT = gql`
  fragment MenuInfo_MenuItemFields on menu_items {
    item_name
    menu_item_ingredient_metadata {
      ...MenuInfo_MenuItemIngredientMetadataFields
    }
    price_in_micros
    uuid
  }

  ${MENU_ITEM_INGREDIENT_METADATA_FRAGMENT}
`;

export const MENU_INFO_CONTEXT_QUERY = gql`
  query MenuInfo_MenuInfoContext(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    menu_items(
      where: {
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      ...MenuInfo_MenuItemFields
    }
  }

  ${MENU_ITEM_FRAGMENT}
`;

export const ADD_MENU_ITEM_MUTATION = gql`
  mutation MenuInfo_AddMenuItem(
    $employeeUUID: uuid!
    $itemName: String!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    insert_menu_items_one(
      object: {
        created_by_uuid: $employeeUUID
        item_name: $itemName
        restaurant_uuid: $restaurantUUID
        supplier_uuid: $supplierUUID
      }
    ) {
      ...MenuInfo_MenuItemFields
    }
  }

  ${MENU_ITEM_FRAGMENT}
`;

export const ADD_MENU_ITEM_INGREDIENT_MUTATION = gql`
  mutation MenuInfo_AddMenuItemIngredient(
    $locale: String!
    $manualIngredient: ManualIngredientFields
    $menuItemUUID: uuid!
    $menuItemIngredientUUID: uuid
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $variantPackCostInMicros: bigint
    $variantPackUUID: uuid
  ) {
    addMenuItemIngredient(
      manual_ingredient: $manualIngredient
      menu_item_id: $menuItemUUID
      menu_item_ingredient_id: $menuItemIngredientUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_cost_in_micros: $variantPackCostInMicros
      variant_pack_id: $variantPackUUID
    ) {
      menu_item_ingredient {
        ...MenuInfo_MenuItemIngredientFields
        menu_item_ingredient_metadata {
          ...MenuInfo_MenuItemIngredientMetadataFields
        }
      }
    }
  }

  ${MENU_ITEM_INGREDIENT_FRAGMENT}
  ${MENU_ITEM_INGREDIENT_METADATA_FRAGMENT}
`;

export const EDIT_MENU_ITEM_INGREDIENT_MUTATION = gql`
  mutation MenuInfo_EditMenuItemIngredient(
    $ingredientFields: EditIngredientFields!
    $locale: String!
    $menuItemIngredientUUID: uuid!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    editMenuItemIngredient(
      ingredient_fields: $ingredientFields
      menu_item_ingredient_id: $menuItemIngredientUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      menu_item_ingredient {
        ...MenuInfo_MenuItemIngredientFields
        menu_item_ingredient_metadata {
          ...MenuInfo_MenuItemIngredientMetadataFields
        }
      }
    }
  }

  ${MENU_ITEM_INGREDIENT_FRAGMENT}
  ${MENU_ITEM_INGREDIENT_METADATA_FRAGMENT}
`;

export const REMOVE_MENU_ITEM_INGREDIENT_METADATA_MUTATION = gql`
  mutation MenuInfo_RemoveMenuItemIngredientMetadata(
    $ingredientMetadataUUID: uuid!
  ) {
    delete_menu_item_ingredient_metadata_by_pk(uuid: $ingredientMetadataUUID) {
      uuid
    }
  }
`;

export const RENAME_MENU_ITEM_MUTATION = gql`
  mutation MenuInfo_RenameMenuItem($itemName: String!, $menuItemUUID: uuid!) {
    update_menu_items_by_pk(
      _set: { item_name: $itemName }
      pk_columns: { uuid: $menuItemUUID }
    ) {
      item_name
      uuid
    }
  }
`;

export const DELETE_MENU_ITEM_MUTATION = gql`
  mutation MenuInfo_DeleteMenuItem(
    $menuItemUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    deleteMenuItem(
      menu_item_id: $menuItemUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      success
    }
  }
`;

export const DUPLICATE_MENU_ITEM_MUTATION = gql`
  mutation MenuInfo_DuplicateMenuItem(
    $locale: String!
    $menuItemUUID: uuid!
    $menuItemName: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    duplicateMenuItem(
      menu_item_id: $menuItemUUID
      menu_item_name: $menuItemName
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      menu_item {
        ...MenuInfo_MenuItemFields
      }
    }
  }
  ${MENU_ITEM_FRAGMENT}
`;

export const SET_MENU_ITEM_INGREDIENT_PORTION_MUTATION = gql`
  mutation MenuInfo_SetIngredientPortion(
    $ingredientUUID: uuid!
    $metadata: jsonb!
  ) {
    update_menu_item_ingredient_metadata_by_pk(
      _set: { ingredient_metadata: $metadata }
      pk_columns: { uuid: $ingredientUUID }
    ) {
      ingredient_metadata
      uuid
      menu_item_ingredient_uuid
      menu_item_uuid
    }
  }
`;

export const SET_MENU_ITEM_PLATE_PRICE_MUTATION = gql`
  mutation MenuInfo_SetPlatePrice(
    $menuItemUUID: uuid!
    $priceInMicros: bigint!
  ) {
    update_menu_items_by_pk(
      _set: { price_in_micros: $priceInMicros }
      pk_columns: { uuid: $menuItemUUID }
    ) {
      price_in_micros
      uuid
    }
  }
`;
